import React from 'react';
import { Heading, Text, List, Box, Code } from '@chakra-ui/react';
import { LuCircleHelp } from "react-icons/lu";
import PageTitle from 'components/ui/PageTitle';
import PageContent from 'components/ui/PageContent';

function HelpPage() {
  return (
    <PageContent>
      <PageTitle icon={LuCircleHelp} title="Help & User Manual" />

      <Heading size="lg">Overview</Heading>
      <Text mb={4}>
        This web application provides a user interface for running BLAST searches against genomic databases, 
        viewing results, and generating reports.
      </Text>

      <Heading size="lg">BLAST Search</Heading>
      <Text >To perform a BLAST search:</Text>
      <List.Root ml={8} gap={2} as="ol">
        <List.Item>Select a BLAST program:
                <List.Root ml={4} mt={2}>
                  <List.Item><Code>blastn</Code> - Nucleotide vs. Nucleotide</List.Item>
                  <List.Item><Code>blastp</Code> - Protein vs. Protein</List.Item>
                  <List.Item><Code>blastx</Code> - Translated Nucleotide vs. Protein</List.Item>
                  <List.Item><Code>tblastn</Code> - Protein vs. Translated Nucleotide</List.Item>
                  <List.Item><Code>tblastx</Code> - Translated Nucleotide vs. Translated Nucleotide</List.Item>
                </List.Root></List.Item>
        <List.Item>Enter your query sequence in FASTA format</List.Item>
        <List.Item>Optionally add a job title for reference</List.Item>
        <List.Item>Select samples to search against using the sample selection interface</List.Item>
        <List.Item>Click "Start [PROGRAM]" to submit the search</List.Item>
      </List.Root>
      <Text >You will be redirected to the Search Results page once the job is submitted.</Text>

      <Heading size="lg">Search Results</Heading>
      <Text>The Search Results page shows all BLAST jobs with:</Text>
      <List.Root ml={8} gap={2}>
        <List.Item>Job ID and submission date</List.Item>
        <List.Item>Status (Running, Completed, Failed)</List.Item>
        <List.Item>BLAST program used</List.Item>
        <List.Item>Number of hits found</List.Item>
        <List.Item>Job title/description</List.Item>
      </List.Root>
      <Text mt={2}>Click on any row to view detailed results for that job.</Text>

      <Heading size="lg">Viewing Results</Heading>
      <Text>In the Results page you can:</Text>
      <List.Root ml={8} gap={2}>
        <List.Item>View job details and search statistics</List.Item>
        <List.Item>Toggle display of 100% identity matches</List.Item>
        <List.Item>Select individual results for download</List.Item>
        <List.Item>Download selected results or all results as text files</List.Item>
        <List.Item>View detailed BLAST alignments by expanding result rows</List.Item>
      </List.Root>

      <Heading size="lg">Generate Reports</Heading>
        <Text>To generate sample reports:</Text>
        <List.Root ml={8} gap={2} as="ol">
          <List.Item>Navigate to the Generate Reports page</List.Item>
          <List.Item>Select samples using the sample selection interface</List.Item>
          <List.Item>Click "Generate Sample Metadata Report" to download an Excel file</List.Item>
        </List.Root>

      <Heading size="lg">Sample Selection</Heading>
      <Text>The sample selection interface allows you to:</Text>
      <List.Root ml={8} gap={2}>
        <List.Item>Filter samples by project</List.Item>
        <List.Item>Filter by taxonomy rank</List.Item>
        <List.Item>Search samples by name or taxonomy</List.Item>
        <List.Item>Select multiple samples for searching or reporting</List.Item>
      </List.Root>

      {/* Only show for superusers */}
      <Heading size="lg">Ingestion Status (Superusers)</Heading>
      <Text>Superusers can monitor sample data ingestion:</Text>
      <List.Root ml={8} gap={2}>
        <List.Item>View all ingestion jobs and their status</List.Item>
        <List.Item>See ingestion dates and affected projects</List.Item>
        <List.Item>Monitor for any errors during data import</List.Item>
        <List.Item>View detailed error messages if ingestion failed</List.Item>
      </List.Root>
    </PageContent>
  );
}

export default HelpPage;